import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Checkbox from '@decisiv/ui-components/lib/components/Checkbox';
import RadioGroup from '@decisiv/ui-components/lib/components/RadioGroup';

/**
 * Checkbox and RadioGroup that actually show their state inside the wizard.
 *
 * KeyDS Wizard renders through a portal, and on the React 16 that portal pins, a
 * controlled boolean input inside one never gets its DOM property updated: React's
 * value tracker desyncs, so the vdom says checked while the input stays unchecked.
 * The state itself is fine — only the rendered control lies. Reduced to:
 *
 *   controlled <Checkbox> clicked outside a Wizard -> input.checked === true
 *   the same component clicked inside a Wizard      -> input.checked === false
 *
 * The same fault hits RadioGroup, which is how "Management Role" lost its
 * Member/Manager selection. It is the third React-16 portal defect this page has
 * had to work around — see the notes in ManagementLayout and FilterMenu.
 *
 * So the DOM property is written back from a ref after every commit. A remount
 * (keying the input on its own value) also fixes it, but throws focus away on
 * each click, which breaks keyboard use.
 *
 * Drop both of these the moment portal is off React 16, and check whether KeyDS
 * still needs the help.
 */

/** Forces `node.checked`, which is the whole point of these wrappers. */
const sync = (node, checked) => {
  if (node && node.checked !== checked) {
    // eslint-disable-next-line no-param-reassign
    node.checked = checked;
  }
};

export function WizardCheckbox({ checked, ...rest }) {
  const inputRef = useRef(null);

  // Deliberately unconditional rather than keyed on `checked`: the property can
  // drift on any commit inside the portal, not only when the value changes.
  useEffect(() => sync(inputRef.current, checked));

  return <Checkbox checked={checked} inputRef={inputRef} {...rest} />;
}

WizardCheckbox.propTypes = {
  checked: PropTypes.bool,
};

WizardCheckbox.defaultProps = {
  checked: false,
};

export function WizardRadioGroup({ items, value, ...rest }) {
  const refs = useRef({});

  useEffect(() => {
    items.forEach((item) =>
      sync(refs.current[item.value], item.value === value),
    );
  });

  return (
    <RadioGroup
      {...rest}
      items={items.map((item) => ({
        ...item,
        // RadioGroup spreads each item into its BooleanInput, which is what lets
        // a per-item ref through.
        inputRef: (node) => {
          refs.current[item.value] = node;
        },
      }))}
      value={value}
    />
  );
}

WizardRadioGroup.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

WizardRadioGroup.defaultProps = {
  value: undefined,
};
